import { Button, ReceiptsFilters, ReceiptsTable, StatusMessage, TimeZoneSelect, Workbench, type DayRange, type ReceiptsTableRow } from '@krp/ui-kit'
import { analyticsView, receiptColor } from '../model/analytics'
import type { CashierDirectory } from '../../cashier-groups'
import { SalesBreakdown } from './SalesBreakdown'
import { SourceProblems } from './SourceProblems'
import type { AnalyticsPage, AnalyticsReceipt, AnalyticsSummary } from '../model/types'
import './cashiers.css'
import type { ReactNode } from 'react'

export interface CashierReportProps {
  directory: CashierDirectory | undefined
  personId: string
  range: DayRange
  today: string
  timeZoneId: string
  place: string
  kind: string
  search: string
  summary: AnalyticsSummary | undefined
  page: AnalyticsPage | undefined
  offset: number
  busy: boolean
  /** Месяц для графика ещё читается, хотя итоги выбранного дня уже на экране; нет — график занят, когда занят экран. */
  trendBusy?: boolean
  pageBusy: boolean
  error: string | null
  patch: (value: Partial<{ personId: string; from: string; to: string; timeZoneId: string; place: string; kind: string; search: string; offset: number }>) => void
  reload: () => void
  links: () => void
  /** «Подробнее»: окно чека источника по адресу, который отдала служба. */
  original: (path: string) => void
  /** Товары чека под раскрытой строкой (их читает binding источника); нет — строки не раскрываются. */
  receiptLines?: (item: AnalyticsReceipt) => ReactNode
  cashierOptions?: readonly { id: string; label: string }[]
  scope?: ReactNode
  graphActions?: ReactNode
  trendContent?: ReactNode
  canReload?: boolean
  cashierNotice?: ReactNode
  cashierControl?: ReactNode
  /** Лента чеков раскрыта; в сводке по всем источникам она свёрнута — главное там продажи по дням. */
  receiptsOpen?: boolean
  /** Таблица «Продажи по дням» по источникам (статистика кассира). */
  dailyTable?: boolean
}
interface ReportReceiptRow extends ReceiptsTableRow { item: AnalyticsReceipt; state: string }

/** Чек отчёта строкой общей таблицы: не подтверждённый оборудованием — приглушён и не в выручке, причина — в «Состоянии». */
const reportRowOf = (item: AnalyticsReceipt, timeZoneId: string): ReportReceiptRow => {
  const receipt = item.receipt
  const pending = item.disposition !== undefined && item.disposition !== 'confirmed'
  return {
    id: `${item.resourceId}:${receipt.key}`, item, refund: receipt.isRefund, number: receipt.number,
    at: receipt.at === null ? 'не указано' : new Date(receipt.at).toLocaleString('ru-RU', { timeZone: timeZoneId }),
    register: receipt.registerName, cashier: receipt.cashierName, total: receipt.amount, cash: receipt.cashAmount, card: receipt.cardAmount,
    muted: pending,
    state: !pending ? 'В выручке' : item.disposition === 'unknown' ? 'Исход неизвестен · не в выручке' : item.disposition === 'rejected' ? 'Отклонён · не в выручке' : 'Не подтверждён · не в выручке',
  }
}

export function CashierReportScreen(props: CashierReportProps) {
  const rows = (props.page?.items ?? []).map((item) => reportRowOf(item, props.timeZoneId))
  const view = analyticsView(props.summary, props.range.from, props.range.to, props.place)
  const people = (props.directory?.people ?? []).filter((person) => person.unified)
  const sourceName = (id: string) => props.summary?.sources.find((source) => source.resourceId === id)?.name ?? id
  const registers = [...new Map((props.summary?.days ?? []).map((day) => [`register:${day.resourceId}:${day.registerKey}`, day])).entries()]
  const receiptLines = props.receiptLines
  const cashiers = props.cashierOptions ?? [{ id: '', label: 'Выберите кассира' }, ...people.map((person) => ({ id: person.id, label: person.name }))]
  const cashierActions = <>
    <Button actionId="receipts.cashier-analytics.reload" busy={props.busy} disabled={!(props.canReload ?? props.personId !== '')} onClick={props.reload}>Обновить из источников</Button>
    {props.directory?.canManage === true && <Button actionId="receipts.cashier-analytics.links" onClick={props.links}>Связи кассиров</Button>}
    {props.cashierNotice}
  </>
  // Колонка «Где смотрим» — только когда экран принёс свой отбор (журнал собственной кассы); список источников кассира не повторяем:
  // источники и кассы уже видны в таблицах «Итоги по кассам» и «Итоги по дням». Часовой пояс — маленький выбор справа вверху.
  const frame = (children: ReactNode) => props.scope !== undefined
    ? <Workbench sideTitle="Где смотрим" sideSummary={props.place === '' ? 'Все доступные источники' : 'Выбранное место продажи'} side={props.scope}>{children}</Workbench>
    : <div className="krp-workbench__main">{children}</div>
  return <section className="cashier-analytics" aria-label={props.cashierOptions === undefined ? 'Чеки по общим кассирам' : 'Чеки и статистика по отбору'}>
    {props.error !== null && <StatusMessage kind="error" title="Не удалось получить данные" text={props.error} />}
    {props.cashierOptions === undefined && props.directory !== undefined && people.length === 0 ? <div className="cashier-analytics__empty">
      <h2>Объедините кассиров из разных источников</h2>
      <p>Создайте карточку человека и свяжите его записи минимум из двух источников. Здесь появятся общий график, итоги и чеки.</p>
      {props.directory.canManage && <Button actionId="receipts.cashier-analytics.links" onClick={props.links}>Связать кассиров</Button>}
    </div> : frame(<>
      <TimeZoneSelect value={props.timeZoneId} onChange={(timeZoneId) => props.patch({ timeZoneId })} />
      <ReceiptsFilters today={props.today} period={props.range} maxDays={31} onPeriod={(range) => props.patch(range)}
        cashiers={cashiers}
        cashier={props.personId} onCashier={(personId) => props.patch({ personId, place: '' })}
        {...(props.cashierControl === undefined ? {} : { cashierControl: props.cashierControl })}
        cashierActions={cashierActions}
        stats={view.stats} statsBusy={props.busy} trend={view.trend} trendBusy={props.trendBusy ?? props.busy}
        {...(props.trendContent !== undefined ? { trendContent: props.trendContent } : {})}
        {...(props.graphActions !== undefined ? { trendActions: props.graphActions } : {})}
        {...(props.summary === undefined && !props.busy ? { trendMessage: props.cashierOptions === undefined ? 'Выберите объединённого кассира' : 'Выберите кассира или источник' } : {})}
        kinds={props.page === undefined ? [] : [{ id: '', label: 'Все чеки' }, { id: 'sale', label: 'Приход' }, { id: 'refund', label: 'Возврат' }]} kind={props.kind}
        onKind={(kind) => props.patch({ kind })} search={props.search} searchLabel="Номер чека" onSearch={(search) => props.patch({ search })}
        days={[]} onDay={(day) => props.patch({ from: day, to: day })}
        notices={<SourceProblems summary={props.summary} timeZoneId={props.timeZoneId} />}
        searchShown={props.page !== undefined} />
      <div>
      {props.dailyTable === true ? <SalesBreakdown summary={props.summary} from={props.range.from} to={props.range.to} place={props.place} /> : null}
      <div className="cashier-analytics__legend" aria-label="Обозначения источников и касс">{registers.map(([key, day]) =>
        <span key={key}><i style={{ backgroundColor: receiptColor(day.resourceId, day.registerKey) }} aria-hidden="true" />{sourceName(day.resourceId)} · {day.registerName}</span>)}</div>
      <div aria-busy={props.pageBusy}>
        <details open={props.receiptsOpen ?? true}><summary>{props.page === undefined ? 'Чеки по отбору' : `Чеки по отбору: ${props.page.total}`}</summary>
        {props.pageBusy && <p role="status">Загружаем чеки…</p>}
        <ReceiptsTable label={props.cashierOptions === undefined ? 'Чеки общего кассира' : 'Чеки по отбору'} rows={rows}
          onDetails={(row) => props.original(row.item.receipt.originalPath)}
          {...(receiptLines === undefined ? {} : { renderLines: (row: ReportReceiptRow) => receiptLines(row.item) })}
          source={(row) => ({ name: row.item.resourceName, color: receiptColor(row.item.resourceId, row.item.receipt.registerKey) })} columns={[
            { id: 'shop', label: 'Магазин', cell: (row) => row.item.receipt.shopName, title: (row) => row.item.receipt.shopName },
            { id: 'state', label: 'Состояние', cell: (row) => row.state, title: (row) => row.state },
          ]} />
        {props.page?.total === 0 && <p>В выбранном диапазоне чеков нет.</p>}
        </details>
      </div>
      <div className="cashier-analytics__toolbar">
        <Button actionId="receipts.cashier-analytics.page" disabled={props.offset === 0 || props.pageBusy} onClick={() => props.patch({ offset: Math.max(0, props.offset - 50) })}>Предыдущие</Button>
        <Button actionId="receipts.cashier-analytics.page" disabled={props.page?.nextOffset == null || props.pageBusy} onClick={() => props.patch({ offset: props.page?.nextOffset ?? 0 })}>Следующие</Button>
      </div>
      </div>
    </>)}
  </section>
}
